import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiJson } from "@/lib/api-client";

export type CloudComputerState =
  | "running"
  | "stopped"
  | "starting"
  | "stopping"
  | "restarting"
  | "resizing"
  | "error"
  | "other";

export type CloudComputer = {
  provider: string;
  id: string;
  name: string;
  state: CloudComputerState;
  providerState: string;
  size?: string;
  region?: string;
  os?: string;
  publicIps: string[];
  privateIps: string[];
  createdAt?: string;
};

export type CloudComputerProvider = {
  id: string;
  label: string;
  configured: boolean;
  error?: string;
  computers: CloudComputer[];
};

export type CloudComputerAction = "start" | "stop" | "restart";

export const CLOUD_COMPUTERS_KEY = ["cloud-computers"] as const;

const ACTION_LABELS: Record<CloudComputerAction, string> = {
  start: "Starting",
  stop: "Stopping",
  restart: "Restarting",
};

export function useCloudComputers() {
  return useQuery({
    queryKey: CLOUD_COMPUTERS_KEY,
    queryFn: () =>
      apiJson<{ providers: CloudComputerProvider[] }>("/api/cloud-computers"),
    refetchInterval: 15000,
  });
}

export function useCloudComputerAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      computer,
      action,
    }: {
      computer: CloudComputer;
      action: CloudComputerAction;
    }) =>
      apiJson(
        `/api/cloud-computers/${computer.provider}/${encodeURIComponent(computer.id)}/${action}`,
        { method: "POST" },
      ),
    onSuccess: (_data, { computer, action }) => {
      queryClient.invalidateQueries({ queryKey: CLOUD_COMPUTERS_KEY });
      toast.success(`${ACTION_LABELS[action]} ${computer.name}`);
    },
    onError: (error) => toast.error(error.message),
  });
}

export type CloudComputerOption = {
  id: string;
  label: string;
  diskGb?: number;
  minDiskGb?: number;
};

export type CloudComputerCreateOptions = {
  sizes: CloudComputerOption[];
  images: CloudComputerOption[];
  regions: CloudComputerOption[];
  networks: CloudComputerOption[];
  keyPairs: CloudComputerOption[];
  securityGroups: CloudComputerOption[];
  defaultSecurityGroups: string[];
  resizeRequiresStop: boolean;
  diskSizesGb: number[] | null;
};

export type CreateCloudComputerInput = {
  name: string;
  sizeId: string;
  imageId: string;
  regionId?: string;
  networkId?: string;
  keyPair?: string;
  securityGroups?: string[];
  diskSizeGb?: number;
  publicIp: boolean;
};

export function useCloudComputerCreateOptions(providerId: string | null) {
  return useQuery({
    queryKey: [...CLOUD_COMPUTERS_KEY, "options", providerId],
    queryFn: () =>
      apiJson<{ options: CloudComputerCreateOptions }>(
        `/api/cloud-computers/${providerId}/options`,
      ),
    enabled: Boolean(providerId),
    staleTime: 5 * 60_000,
  });
}

export function useCreateCloudComputer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      providerId,
      input,
    }: {
      providerId: string;
      input: CreateCloudComputerInput;
    }) =>
      apiJson<{ id: string; warning?: string }>(
        `/api/cloud-computers/${providerId}`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: (result, { input }) => {
      queryClient.invalidateQueries({ queryKey: CLOUD_COMPUTERS_KEY });
      if (result.warning) toast.warning(result.warning);
      else toast.success(`Creating ${input.name}`);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteCloudComputer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      computer,
      confirmName,
      releasePublicIp,
    }: {
      computer: CloudComputer;
      confirmName: string;
      releasePublicIp: boolean;
    }) =>
      apiJson(
        `/api/cloud-computers/${computer.provider}/${encodeURIComponent(computer.id)}`,
        {
          method: "DELETE",
          body: JSON.stringify({ confirmName, releasePublicIp }),
        },
      ),
    onSuccess: (_data, { computer }) => {
      queryClient.invalidateQueries({ queryKey: CLOUD_COMPUTERS_KEY });
      toast.success(`Deleting ${computer.name}`);
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useResizeCloudComputer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      computer,
      sizeId,
    }: {
      computer: CloudComputer;
      sizeId: string;
      sizeLabel: string;
    }) =>
      apiJson(
        `/api/cloud-computers/${computer.provider}/${encodeURIComponent(computer.id)}/resize`,
        { method: "POST", body: JSON.stringify({ sizeId }) },
      ),
    onSuccess: (_data, { computer, sizeLabel }) => {
      queryClient.invalidateQueries({ queryKey: CLOUD_COMPUTERS_KEY });
      toast.success(`Resizing ${computer.name} to ${sizeLabel}`);
    },
    onError: (error) => toast.error(error.message),
  });
}
